import { Navigate, Route, createBrowserRouter, createRoutesFromElements } from "react-router-dom";

import {
  AssetPersonalDataView,
  AssetsView,
  BankruptcyCapability,
  BankruptcyConsequences,
  DangerousDebts,
  DonationsView,
  ExpensesView,
  IncomeView,
  OtherPropertyRightsView,
  OwnClaimsView,
  RelativesView,
  SpecialDebts,
} from "@shared";

import { Layout } from "@/common/components/layout/Layout";
import { Ambulanzes } from "@/pages/ambulanzes/Ambulanzes";
import { AppointmentsOverview } from "@/pages/appointments-overview/AppointmentsOverview";
import { Assignments } from "@/pages/assignments/Assignments";
import { AttendanceList } from "@/pages/attendance-list/AttendanceList";
import { Calculators } from "@/pages/calculators/Calculators";
import { Client } from "@/pages/clients/client/Client";
import { ClientNoteTab } from "@/pages/clients/client/components/tabs/client-note-tab/ClientNoteTab";
import { DebtSettlementTab } from "@/pages/clients/client/components/tabs/debt-settlement-tab/DebtSettlementTab";
import { AssetInventoryTab } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/asset-inventory-tab/AssetInventoryTab";
import { DocumentsOverviewView } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/asset-inventory-tab/views/documents-overview/DocumentsOverviewView";
import { BankruptcyChecklist } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/bankruptcy-checklist-tab/BankruptcyChecklist";
import { ClaimListTab } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/claim-list-tab/ClaimListTab";
import { CompensationTab } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/compensation-tab/CompensationTab";
import { PersonalBankruptcyPetitionTab } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/personal-bankruptcy-petition-tab/PersonalBankruptcyPetitionTab";
import { SrvProceedingTab } from "@/pages/clients/client/components/tabs/debt-settlement-tab/tabs/srv-proceeding-tab/SrvProceedingTab";
import { MasterDataTab } from "@/pages/clients/client/components/tabs/master-data-tab/MasterDataTab";
import { ClientAccessView } from "@/pages/clients/client/components/tabs/master-data-tab/views/client-access/ClientAccessView";
import { GoalsResultsView } from "@/pages/clients/client/components/tabs/master-data-tab/views/goals-results/GoalsResultsView";
import { MeasuresCausesView } from "@/pages/clients/client/components/tabs/master-data-tab/views/measures-causes/MeasuresCausesView";
import { PersonalDataView } from "@/pages/clients/client/components/tabs/master-data-tab/views/personal-data/PersonalDataView";
import { StatusCounselorView } from "@/pages/clients/client/components/tabs/master-data-tab/views/status-counselor/StatusCounselorView";
import { VolunteerAssignmentView } from "@/pages/clients/client/components/tabs/master-data-tab/views/volunteer-assignment/VolunteerAssignmentView";
import { CaseNotesTab } from "@/pages/clients/client/components/tabs/notes-tab/CaseNotesTab";
import { CaseNotePopout } from "@/pages/clients/client/components/tabs/notes-tab/pages/case-note-popout/CaseNotePopout";
import { OverviewTab } from "@/pages/clients/client/components/tabs/overview-tab/OverviewTab";
import { Clients } from "@/pages/clients/clients/Clients";
import { CourtHearings } from "@/pages/court-hearings/CourtHearings";
import { DutySchedule } from "@/pages/duty-schedule/DutySchedule";
import { DutyScheduleDetail } from "@/pages/duty-schedule/pages/duty-schedule-detail/DutyScheduleDetail";
import { DutyScheduleCounselorOverview } from "@/pages/duty-schedule/pages/duty-schedule-detail/pages/counselor-overview/DutyScheduleCounselorOverview";
import { DutyScheduleDataEntry } from "@/pages/duty-schedule/pages/duty-schedule-detail/pages/data-entry/DutyScheduleDataEntry";
import { DutyScheduleDistribution } from "@/pages/duty-schedule/pages/duty-schedule-detail/pages/distribution/DutyScheduleDistribution";
import { Edicts } from "@/pages/edicts/Edicts";
import { Exchange } from "@/pages/exchange/Exchange";
import { Home } from "@/pages/home/Home";
import { InternetRegistration } from "@/pages/internet-registration/InternetRegistration";
import { PriorityRequests } from "@/pages/priority-requests/PriorityRequests";
import { Statistics } from "@/pages/statistics/Statistics";
import { SystemSettings } from "@/pages/system-settings/SystemSettings";
import { AddressesSettings } from "@/pages/system-settings/tabs/addresses/AddressesSettings";
import { AmsParametersSettings } from "@/pages/system-settings/tabs/ams-parameters/AmsParametersSettings";
import { BannerConfiguration } from "@/pages/system-settings/tabs/banner-configuration/BannerConfiguration";
import { CalculationParametersSettings } from "@/pages/system-settings/tabs/calculation-parameters/CalculationParametersSettings";
import { CaseNoteTemplatesSettings } from "@/pages/system-settings/tabs/case-note-templates/CaseNoteTemplatesSettings";
import { CounselorList } from "@/pages/system-settings/tabs/counselors/CounselorList";
import { CounselorDetail } from "@/pages/system-settings/tabs/counselors/pages/counselor-detail/CounselorDetail";
import { CounselorDutyPreferences } from "@/pages/system-settings/tabs/counselors/pages/counselor-detail/pages/duty-preferences/CounselorDutyPreferences";
import { CounselorPersonalData } from "@/pages/system-settings/tabs/counselors/pages/counselor-detail/pages/personal-data/CounselorPersonalData";
import { CounselorWorkTime } from "@/pages/system-settings/tabs/counselors/pages/counselor-detail/pages/work-time/CounselorWorkTime";
import { ViennaCourts } from "@/pages/system-settings/tabs/courts/vienna/ViennaCourts";
import { DropdownsSettings } from "@/pages/system-settings/tabs/dropdowns/DropdownsSettings";
import { EmailTemplatesSettings } from "@/pages/system-settings/tabs/email-templates/EmailTemplatesSettings";
import { HolidaysSettings } from "@/pages/system-settings/tabs/holidays/HolidaysSettings";
import { MailMergeSettings } from "@/pages/system-settings/tabs/mail-merge/MailMergeSettings";
import { ProceedingParticipants } from "@/pages/system-settings/tabs/proceeding-participants/ProceedingParticipants";
import { TemplatesSettings } from "@/pages/system-settings/tabs/sms-templates/SmsTemplatesSettings";
import { SystemDropdownsSettings } from "@/pages/system-settings/tabs/system-dropdowns/SystemDropdownsSettings";
import { TemplatesTab } from "@/pages/system-settings/tabs/templates/TemplatesTab";
import { TranslationToolSettings } from "@/pages/system-settings/tabs/translation-tool/TranslationToolSettings";
import { Volunteers } from "@/pages/system-settings/volunteers/Volunteers";
import { VolunteerDetail } from "@/pages/system-settings/volunteers/pages/volunteer-detail/VolunteerDetail";
import { VolunteerAccess } from "@/pages/system-settings/volunteers/pages/volunteer-detail/pages/access/VolunteerAccess";
import { VolunteerAssignedClients } from "@/pages/system-settings/volunteers/pages/volunteer-detail/pages/assigned-clients/VolunteerAssignedClients";
import { VolunteerPersonalData } from "@/pages/system-settings/volunteers/pages/volunteer-detail/pages/personal-data/VolunteerPersonalData";
import { Takeover } from "@/pages/takeover/Takeover";

import { AppRoutes } from "./AppRoutes";
import {
  AssetInventorySubTab,
  BankruptcyChecklistSubTab,
  CASE_NOTE_POPOUT_SEGMENT,
  ClientMasterDataTab,
  ClientRoutes,
  ClientTab,
  DebtSettlementSubTab,
} from "./ClientRoutes";
import { DutyScheduleRoutes, DutyScheduleTab } from "./DutyScheduleRoutes";
import { RouterProviders } from "./RouterProviders";
import {
  CounselorRoutes,
  SystemSettingsCounselorTab,
  SystemSettingsCourtsTab,
  SystemSettingsTab,
  SystemSettingsTemplateTab,
  SystemSettingsVolunteerTab,
  VolunteerRoutes,
} from "./SystemSettingsRoutes";

const BASE_PATH = import.meta.env.VITE_APP_BASE_PATH;

export const router = createBrowserRouter(
  createRoutesFromElements(
    <Route element={<RouterProviders />}>
      <Route path={AppRoutes.Default} element={<Layout />}>
        <Route index element={<Home />} />
        <Route path={AppRoutes.Clients}>
          <Route index element={<Clients />} />
          <Route path={ClientRoutes.ById} element={<Client />}>
            <Route index element={<Navigate to={ClientTab.MasterData} replace />} />
            <Route path={ClientTab.MasterData} element={<MasterDataTab />}>
              <Route index element={<Navigate to={ClientMasterDataTab.PersonalData} replace />} />
              <Route path={ClientMasterDataTab.PersonalData} element={<PersonalDataView />} />
              <Route path={ClientMasterDataTab.GoalsResults} element={<GoalsResultsView />} />
              <Route path={ClientMasterDataTab.MeasuresCauses} element={<MeasuresCausesView />} />
              <Route path={ClientMasterDataTab.StatusCounselor} element={<StatusCounselorView />} />
              <Route path={ClientMasterDataTab.VolunteerAssignment} element={<VolunteerAssignmentView />} />
              <Route path={ClientMasterDataTab.ClientAccess} element={<ClientAccessView />} />
            </Route>
            <Route path={ClientTab.Overview} element={<OverviewTab />} />
            <Route path={ClientTab.ClientNote} element={<ClientNoteTab />} />
            <Route path={ClientTab.Notes} element={<CaseNotesTab />} />
            <Route path={ClientTab.DebtSettlement} element={<DebtSettlementTab />}>
              <Route index element={<Navigate to={DebtSettlementSubTab.AssetInventory} replace />} />
              <Route path={DebtSettlementSubTab.AssetInventory} element={<AssetInventoryTab />}>
                <Route index element={<Navigate to={AssetInventorySubTab.PersonalData} replace />} />
                <Route path={AssetInventorySubTab.PersonalData} element={<AssetPersonalDataView />} />
                <Route path={AssetInventorySubTab.Relatives} element={<RelativesView />} />
                <Route path={AssetInventorySubTab.Income} element={<IncomeView />} />
                <Route path={AssetInventorySubTab.Expenses} element={<ExpensesView />} />
                <Route path={AssetInventorySubTab.OwnClaims} element={<OwnClaimsView />} />
                <Route path={AssetInventorySubTab.Assets} element={<AssetsView />} />
                <Route path={AssetInventorySubTab.OtherPropertyRights} element={<OtherPropertyRightsView />} />
                <Route path={AssetInventorySubTab.Donations} element={<DonationsView />} />
                <Route path={AssetInventorySubTab.Documents} element={<DocumentsOverviewView />} />
              </Route>
              <Route path={DebtSettlementSubTab.Claims} element={<ClaimListTab />} />
              <Route path={DebtSettlementSubTab.BankruptcyChecklist} element={<BankruptcyChecklist />}>
                <Route index element={<Navigate to={BankruptcyChecklistSubTab.DangerousDebts} replace />} />
                <Route path={BankruptcyChecklistSubTab.DangerousDebts} element={<DangerousDebts />} />
                <Route path={BankruptcyChecklistSubTab.SpecialDebts} element={<SpecialDebts />} />
                <Route path={BankruptcyChecklistSubTab.BankruptcyCapability} element={<BankruptcyCapability />} />
                <Route path={BankruptcyChecklistSubTab.BankruptcyConsequences} element={<BankruptcyConsequences />} />
              </Route>
              <Route path={DebtSettlementSubTab.Compensation} element={<CompensationTab />} />
              <Route
                path={DebtSettlementSubTab.PersonalBankruptcyPetition}
                element={<PersonalBankruptcyPetitionTab />}
              />
              <Route path={DebtSettlementSubTab.SrvProceeding} element={<SrvProceedingTab />} />
            </Route>
          </Route>
        </Route>
        <Route path={AppRoutes.Appointments} element={<AppointmentsOverview />} />
        <Route path={AppRoutes.AttendanceList} element={<AttendanceList />} />
        <Route path={AppRoutes.CourtHearings} element={<CourtHearings />} />
        <Route path={AppRoutes.Exchange} element={<Exchange />} />
        <Route path={AppRoutes.Takeover} element={<Takeover />} />
        <Route path={AppRoutes.InternetRegistration} element={<InternetRegistration />} />
        <Route path={AppRoutes.SystemSettings}>
          <Route element={<SystemSettings />}>
            <Route index element={<Navigate to={SystemSettingsTab.Counselors} replace />} />
            <Route path={SystemSettingsTab.Dropdowns} element={<DropdownsSettings />} />
            <Route path={SystemSettingsTab.Templates} element={<TemplatesTab />}>
              <Route index element={<Navigate to={SystemSettingsTemplateTab.Sms} replace />} />
              <Route path={SystemSettingsTemplateTab.Sms} element={<TemplatesSettings />} />
              <Route path={SystemSettingsTemplateTab.CaseNote} element={<CaseNoteTemplatesSettings />} />
              <Route path={SystemSettingsTemplateTab.Email} element={<EmailTemplatesSettings />} />
            </Route>
            <Route path={SystemSettingsTab.Banners} element={<BannerConfiguration />} />
            <Route path={SystemSettingsTab.TranslationTool} element={<TranslationToolSettings />} />
            <Route path={SystemSettingsTab.MailMerge} element={<MailMergeSettings />} />
            <Route path={SystemSettingsTab.Addresses} element={<AddressesSettings />} />
            <Route path={SystemSettingsTab.Holidays} element={<HolidaysSettings />} />
            <Route path={SystemSettingsTab.ProceedingParticipants} element={<ProceedingParticipants />} />
            <Route path={SystemSettingsTab.Volunteers}>
              <Route index element={<Volunteers />} />
              <Route path={VolunteerRoutes.ById} element={<VolunteerDetail />}>
                <Route index element={<Navigate to={SystemSettingsVolunteerTab.PersonalData} replace />} />
                <Route path={SystemSettingsVolunteerTab.PersonalData} element={<VolunteerPersonalData />} />
                <Route path={SystemSettingsVolunteerTab.AssignedClients} element={<VolunteerAssignedClients />} />
                <Route path={SystemSettingsVolunteerTab.Access} element={<VolunteerAccess />} />
              </Route>
            </Route>
            <Route path={SystemSettingsTab.SystemRelevantDropdowns} element={<SystemDropdownsSettings />} />
            <Route path={SystemSettingsTab.Parameters} element={<CalculationParametersSettings />} />
            <Route path={SystemSettingsTab.AmsParameters} element={<AmsParametersSettings />} />
            <Route path={SystemSettingsTab.Counselors}>
              <Route index element={<CounselorList />} />
              <Route path={CounselorRoutes.ById} element={<CounselorDetail />}>
                <Route index element={<Navigate to={SystemSettingsCounselorTab.PersonalData} replace />} />
                <Route path={SystemSettingsCounselorTab.PersonalData} element={<CounselorPersonalData />} />
                <Route path={SystemSettingsCounselorTab.WorkTime} element={<CounselorWorkTime />} />
                <Route path={SystemSettingsCounselorTab.DutyPreferences} element={<CounselorDutyPreferences />} />
              </Route>
            </Route>
            <Route path={SystemSettingsTab.Courts}>
              <Route index element={<Navigate to={SystemSettingsCourtsTab.Vienna} replace />} />
              <Route path={SystemSettingsCourtsTab.Vienna} element={<ViennaCourts />} />
            </Route>
          </Route>
        </Route>
        <Route path={AppRoutes.Statistic} element={<Statistics />} />
        <Route path={AppRoutes.Assignments} element={<Assignments />} />
        <Route path={AppRoutes.Ambulanzes} element={<Ambulanzes />} />
        <Route path={AppRoutes.PriorityRequests} element={<PriorityRequests />} />
        <Route path={AppRoutes.Edicts} element={<Edicts />} />
        <Route path={AppRoutes.Calculators} element={<Calculators />} />
        <Route path={AppRoutes.DutySchedule} element={<DutySchedule />} />
        <Route path={`${AppRoutes.DutySchedule}/${DutyScheduleRoutes.ById}`} element={<DutyScheduleDetail />}>
          <Route index element={<Navigate to={DutyScheduleTab.DataEntry} replace />} />
          <Route path={DutyScheduleTab.DataEntry} element={<DutyScheduleDataEntry />} />
          <Route path={DutyScheduleTab.CounselorOverview} element={<DutyScheduleCounselorOverview />} />
          <Route path={DutyScheduleTab.Distribution} element={<DutyScheduleDistribution />} />
        </Route>
      </Route>
      <Route
        path={`${AppRoutes.Clients}/${ClientRoutes.ById}/${ClientTab.Notes}/${ClientRoutes.NoteById}/${CASE_NOTE_POPOUT_SEGMENT}`}
        element={<CaseNotePopout />}
      />
      <Route path="*" element={<Navigate to={AppRoutes.Default} replace />} />
    </Route>
  ),
  { basename: BASE_PATH }
);
